'use client';
import {
  APIProvider,
  Map,
  MapCameraChangedEvent,
  type MapCameraProps,
} from '@vis.gl/react-google-maps';
import { useEffect, useMemo, useState } from 'react';

import { ClusteredMarkers } from '@/app/components/shared/dealer/components/shared/google-maps/clustered-markers';
import { DotMarker } from '@/app/components/shared/dealer/components/shared/google-maps/clustered-markers/dot-marker';
import { GoogleMapMessageOverlay } from '@/app/components/shared/dealer/components/shared/google-maps/message-overlay';
import { MapControlsDesktop } from '@/app/components/shared/dealer/components/shared/map-controls/desktop';
import {
  MAP_DEFAULT,
  SearchType,
} from '@/app/components/shared/dealer/utils/dealer-constant';
import type { DealerSearch } from '@/app/components/shared/dealer/utils/types';
import { safeCall } from '@/app/utils/safeCall';

interface DealerGoogleMapsProps {
  dealers?: DealerSearch[];
  selectedDealer?: DealerSearch | null;
  currentLocation: google.maps.LatLngLiteral | null;
  searchType?: SearchType;
  isFromMarker?: boolean;
  suggestPlaceLocation?: google.maps.LatLngLiteral | null;
  onSelectDealer?: (dealerId: string, tab?: string) => void;
  setIsLoadMapSuccess?: (isSuccess: boolean) => void;
  onClickCurrentLocation: () => void;
}

export const DealerGoogleMaps = ({
  selectedDealer,
  dealers = [],
  currentLocation,
  searchType,
  isFromMarker = false,
  suggestPlaceLocation,
  onSelectDealer,
  onClickCurrentLocation,
  setIsLoadMapSuccess,
}: DealerGoogleMapsProps) => {
  const [cameraProps, setCameraProps] = useState<MapCameraProps>({
    center: MAP_DEFAULT.CENTER,
    zoom: MAP_DEFAULT.ZOOM,
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  const handleCameraChange = (ev: MapCameraChangedEvent) => {
    setCameraProps(ev.detail);
  };

  useEffect(() => {
    safeCall(setIsLoadMapSuccess, !isError && !isLoading);
  }, [isError, isLoading, setIsLoadMapSuccess]);

  // Map only ID of each dealer to check if the dealer group set. If value is updated then dealer set is filtered and changed to optimize the useEffect state change
  const dealerUids = useMemo(
    () => dealers.map((item) => item.uid.charAt(item.uid.length - 1)).join(''),
    [dealers]
  );

  return (
    <>
      {!!isLoading && !isError && <GoogleMapMessageOverlay />}
      {!!isError && <GoogleMapMessageOverlay isError />}

      <APIProvider
        apiKey={process.env.NEXT_PUBLIC_GOOGLEMAP_API_KEY as string}
        onLoad={() => {
          setIsLoading(false);
        }}
        onError={() => {
          setIsLoading(false);
          setIsError(true);
        }}
      >
        {!isError && (
          <Map
            id="dealer-map"
            {...cameraProps}
            mapId={process.env.NEXT_PUBLIC_GOOGLEMAP_MAP_ID}
            gestureHandling="greedy"
            disableDefaultUI
            streetViewControl={false}
            fullscreenControl={false}
            mapTypeControl={false}
            minZoom={2}
            onCameraChanged={handleCameraChange}
          >
            {!!currentLocation && <DotMarker position={currentLocation} />}
            <MapControlsDesktop
              currentLocation={currentLocation}
              onClickCurrentLocation={onClickCurrentLocation}
            />
            <ClusteredMarkers
              dealers={dealers}
              dealerUids={dealerUids}
              searchType={searchType}
              isFromMarker={isFromMarker}
              currentLocation={currentLocation}
              selectedDealer={selectedDealer}
              suggestPlaceLocation={suggestPlaceLocation}
              onClickMarker={onSelectDealer}
              onClickCurrentLocation={onClickCurrentLocation}
            />
          </Map>
        )}
      </APIProvider>
    </>
  );
};
